import { useState, type FormEvent } from 'react';
import {
  ActionsDialogue, ActionsLigne, Bouton, Carte, CaseACocher, Champ, Dialogue,
  EnTetePage, IconeCorbeille, IconeCrayon, IconeEleves, IconeImport,
  IconeImprimer, IconePlus, Message, SqueletteTableau, Tableau, Vide, ZoneTexte,
  useRetours,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';

/**
 * Liste de classe.
 *
 * ⚠ Données personnelles de mineurs. Nom, prénom, rien d'autre — la
 * minimisation est une règle du module serveur, l'interface ne doit pas
 * inviter à la contourner en proposant un champ « remarques » ou « date de
 * naissance ».
 *
 * ── L'import remplaçant est destructeur ─────────────────────────────────────
 * Il supprime les élèves de l'année en cours, donc leurs évaluations en
 * cascade. C'est irrémédiable. L'écran le dit avant, chiffre ce qui sera
 * perdu, et n'active le bouton qu'après une confirmation explicite.
 */

export interface Eleve {
  id: string;
  lastName: string;
  firstName: string;
  schoolYearId: string | null;
  position: number;
}

/** Une ligne = un élève. « Nom Prénom » ou « Nom, Prénom ». */
export function analyserListe(texte: string): Array<{ lastName: string; firstName: string }> {
  return texte
    .split('\n')
    .map((ligne) => ligne.trim())
    .filter(Boolean)
    .map((ligne) => {
      if (ligne.includes(',')) {
        const [nom = '', prenom = ''] = ligne.split(',', 2);
        return { lastName: nom.trim(), firstName: prenom.trim() };
      }
      // Sans virgule : le PREMIER mot est le nom de famille. C'est la
      // convention des listes d'appel, et l'inverse — deviner d'après les
      // majuscules — se trompe sur « Le Goff Marie » comme sur « marie dupont ».
      const morceaux = ligne.split(/\s+/);
      const nom = morceaux.shift() ?? '';
      return { lastName: nom, firstName: morceaux.join(' ') };
    })
    .filter((e) => e.lastName !== '');
}

/**
 * Gestion de la classe.
 *
 * Accessible depuis l'écran Évaluation — c'est là qu'elle vit dans
 * l'application d'origine (`gererEleves`), et c'est logique : on ne consulte sa
 * liste d'appel que pour évaluer ou faire l'appel, jamais pour elle-même.
 *
 * `enDialogue` retire l'en-tête de page : dans une fenêtre, le titre est déjà
 * porté par le dialogue, et le répéter décale tout le contenu.
 */
export function Eleves({ enDialogue = false, surChangement }: {
  enDialogue?: boolean;
  /** Après un ajout, une modification, une suppression ou un import. */
  surChangement?: () => void;
} = {}) {
  const { api } = useSession();
  const annee = useAnneeActive();
  const { notifier, confirmer } = useRetours();
  const eleves = useDonnees<Eleve[]>('/students');
  const { executer, enCours, erreur: erreurEcriture } = useEcriture();

  const [edite, setEdite] = useState<Eleve | null>(null);
  const [creation, setCreation] = useState(false);
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');

  const [importOuvert, setImportOuvert] = useState(false);
  const [texteImport, setTexteImport] = useState('');
  const [remplacer, setRemplacer] = useState(false);
  const [remplacementConfirme, setRemplacementConfirme] = useState(false);

  function ouvrirCreation() {
    setNom('');
    setPrenom('');
    setCreation(true);
  }

  function ouvrirEdition(eleve: Eleve) {
    setNom(eleve.lastName);
    setPrenom(eleve.firstName);
    setEdite(eleve);
  }

  function fermerFormulaire() {
    setCreation(false);
    setEdite(null);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    const corps = { lastName: nom.trim(), firstName: prenom.trim() };
    const ok = await executer(() =>
      edite
        ? api.requete(`/students/${edite.id}`, { methode: 'PATCH', corps })
        : api.requete('/students', {
            methode: 'POST',
            /*
             * Pas de rang ici : le serveur range le nouvel élève après le
             * dernier. L'écran le déduisait du nombre d'élèves affichés, ce
             * qui donnait un rang DÉJÀ PRIS après un départ en milieu de
             * liste — et la classe se réordonnait toute seule.
             */
            corps,
          }));
    if (ok) {
      notifier(edite ? 'Élève modifié.' : `${prenom} ${nom} a rejoint la classe.`.trim());
      fermerFormulaire();
      await eleves.recharger();
      surChangement?.();
    }
  }

  async function supprimer(eleve: Eleve) {
    const nomComplet = `${eleve.firstName} ${eleve.lastName}`.trim();
    // La suppression emporte les évaluations : on le dit AVANT, plutôt que de
    // le faire découvrir après coup.
    const ok = await confirmer({
      titre: `Supprimer ${nomComplet} ?`,
      message: 'Ses évaluations seront supprimées avec lui. Cette action est définitive.',
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!ok) return;
    if (await executer(() => api.requete(`/students/${eleve.id}`, { methode: 'DELETE' }))) {
      notifier(`${nomComplet} a été retiré de la classe.`);
      await eleves.recharger();
      surChangement?.();
    }
  }

  async function importer(evenement: FormEvent) {
    evenement.preventDefault();
    const liste = analyserListe(texteImport);
    const ok = await executer(async () => {
      const reponse = await api.requete<{ imported: number; deleted: number }>(
        '/students/import',
        { methode: 'POST', corps: { students: liste, replace: remplacer } },
      );
      notifier(
        reponse.deleted > 0
          ? `${reponse.imported} élève(s) importé(s), ${reponse.deleted} remplacé(s).`
          : `${reponse.imported} élève(s) importé(s).`,
      );
    });
    if (ok) {
      setImportOuvert(false);
      setTexteImport('');
      setRemplacer(false);
      setRemplacementConfirme(false);
      await eleves.recharger();
      surChangement?.();
    }
  }

  if (annee.erreur) {
    return (
      <Carte titre="Élèves">
        <Vide titre="Aucune année scolaire active">
          La liste de classe est rattachée à une année scolaire. Créez-en une
          dans les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (eleves.chargement && !eleves.donnees) {
    return (
      <div className="flex flex-col gap-5">
        {!enDialogue && <EnTetePage titre="Élèves" />}
        <Carte><SqueletteTableau lignes={6} colonnes={4} /></Carte>
      </div>
    );
  }
  if (eleves.erreur) return <Message ton="erreur">{eleves.erreur}</Message>;

  const liste = eleves.donnees ?? [];
  const apercuImport = analyserListe(texteImport);

  return (
    <div className="flex flex-col gap-4">
      {!enDialogue && (
      <EnTetePage
        titre="Élèves"
        contexte={
          annee.donnees
            ? `${annee.donnees.label} · ${liste.length} élève${liste.length > 1 ? 's' : ''}`
            : undefined
        }
        actions={
          <>
            <Bouton
              variante="ghost"
              icone={<IconeImprimer taille={16} />}
              onClick={() => globalThis.print()}
            >
              Imprimer
            </Bouton>
            <Bouton
              variante="secondary"
              icone={<IconeImport taille={16} />}
              onClick={() => setImportOuvert(true)}
            >
              Importer
            </Bouton>
            <Bouton
              variante="primary"
              icone={<IconePlus taille={16} />}
              onClick={ouvrirCreation}
            >
              Ajouter un élève
            </Bouton>
          </>
        }
      />
      )}

      {/* En dialogue, les actions restent nécessaires : elles remontent dans
          une barre, sans l'en-tête de page qui ferait doublon avec le titre. */}
      {enDialogue && (
        <div className="flex flex-wrap items-center gap-2">
          {/* L'effectif reste affiché : c'est le premier contrôle qu'on fait
              en ouvrant sa liste, et l'en-tête de page qui le portait n'est
              pas rendu en dialogue. */}
          <span className="mr-auto text-[13px] text-[var(--color-fg-muted)]">
            {`${liste.length} élève${liste.length > 1 ? 's' : ''}`}
          </span>
          <Bouton
            variante="primary"
            icone={<IconePlus taille={16} />}
            onClick={ouvrirCreation}
          >
            Ajouter un élève
          </Bouton>
          <Bouton
            variante="secondary"
            icone={<IconeImport taille={16} />}
            onClick={() => setImportOuvert(true)}
          >
            Importer
          </Bouton>
        </div>
      )}

      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}

      <Carte>
        <Tableau
          redimensionnable="eleves"
          legende={`Élèves de la classe ${annee.donnees?.label ?? ''}`}
          lignes={liste}
          cleLigne={(e) => e.id}
          zebre={liste.length > 5}
          vide={
            <Vide
              titre="Aucun élève dans cette classe"
              icone={<IconeEleves taille={20} />}
              action={
                <Bouton
                  variante="primary"
                  icone={<IconeImport taille={16} />}
                  onClick={() => setImportOuvert(true)}
                >
                  Importer la liste d'appel
                </Bouton>
              }
            >
              Collez la liste d'appel en une fois, ou ajoutez les élèves un par
              un.
            </Vide>
          }
          colonnes={[
            {
              cle: 'rang',
              entete: '#',
              largeur: '3rem',
              numerique: true,
              // Le rang d'appel : c'est dans cet ordre que l'enseignant fait
              // l'appel, et c'est ce qui permet de repérer une ligne dans une
              // liste de trente noms.
              cellule: (_e, i) => (
                <span className="text-[var(--color-fg-muted)]">{i + 1}</span>
              ),
            },
            {
              cle: 'nom',
              entete: 'Nom',
              cellule: (e) => <span className="font-medium">{e.lastName}</span>,
            },
            { cle: 'prenom', entete: 'Prénom', cellule: (e) => e.firstName },
            {
              cle: 'actions',
              entete: 'Actions',
              actions: true,
              largeur: '1%',
              cellule: (e) => (
                <ActionsLigne>
                  {/* Icônes seules : dans un tableau de trente lignes, deux
                      libellés par ligne pèsent plus que les données. Le nom de
                      l'élève reste dans l'`aria-label`. */}
                  <Bouton
                    variante="discret"
                    taille="sm"
                    iconeSeule
                    icone={<IconeCrayon taille={15} />}
                    onClick={() => ouvrirEdition(e)}
                    aria-label={`Modifier ${e.lastName} ${e.firstName}`.trim()}
                  >
                    Modifier
                  </Bouton>
                  <Bouton
                    variante="discret"
                    taille="sm"
                    iconeSeule
                    icone={<IconeCorbeille taille={15} />}
                    onClick={() => void supprimer(e)}
                    aria-label={`Supprimer ${e.lastName} ${e.firstName}`.trim()}
                  >
                    Supprimer
                  </Bouton>
                </ActionsLigne>
              ),
            },
          ]}
        />
      </Carte>

      <Dialogue
        titre={edite ? 'Modifier un élève' : 'Ajouter un élève'}
        ouvert={creation || edite !== null}
        surFermeture={fermerFormulaire}
      >
        <form onSubmit={enregistrer} className="flex flex-col gap-4">
          <Champ
            libelle="Nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            autoFocus
            required
          />
          <Champ
            libelle="Prénom"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            // Même mention que sur l'import : c'est ici aussi qu'on serait
            // tenté d'ajouter « (dys) » ou « PAI » derrière un prénom.
            aide="Nom et prénom seulement : ni santé, ni suivi, ni situation familiale."
          />
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={fermerFormulaire}>Annuler</Bouton>
            <Bouton type="submit" variante="primary" chargement={enCours}>
              Enregistrer
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>

      <Dialogue
        titre="Importer une liste de classe"
        ouvert={importOuvert}
        surFermeture={() => setImportOuvert(false)}
      >
        <form onSubmit={importer} className="flex flex-col gap-4">
          <ZoneTexte
            libelle="Liste des élèves"
            value={texteImport}
            onChange={(e) => setTexteImport(e.target.value)}
            /*
             * ── Ce qui ne doit PAS être écrit, dit à l'endroit où l'on écrit ─
             *
             * L'application ne stocke d'un élève que son nom et son prénom :
             * ni date de naissance, ni adresse, ni santé. Cette minimisation
             * est une décision de schéma, et elle tient tant que personne ne
             * la contourne par un champ libre.
             *
             * « Dupont Marie (dys) » y suffirait : une donnée de santé, dont
             * le traitement est interdit par principe (RGPD art. 9), inscrite
             * dans un champ prévu pour un patronyme. On ne peut pas
             * l'empêcher techniquement sans rendre la saisie impraticable —
             * on le dit donc là où le geste se fait, et non dans une politique
             * que personne n'ouvre.
             */
            aide={
              'Un élève par ligne : « Dupont Marie » ou « Dupont, Marie ». '
              + 'N’ajoutez rien d’autre : ni santé, ni suivi médical, '
              + 'ni situation familiale.'
            }
            autoFocus
          />

          {texteImport.trim() !== '' && (
            <Message>
              {`${apercuImport.length} élève(s) seront importés. `}
              {apercuImport[0]
                ? `Premier : ${apercuImport[0].lastName} ${apercuImport[0].firstName}.`.trim()
                : ''}
            </Message>
          )}

          <CaseACocher
            libelle="Remplacer la liste actuelle"
            description="Sinon, les élèves sont ajoutés à ceux déjà présents."
            checked={remplacer}
            onChange={(e) => {
              setRemplacer(e.target.checked);
              setRemplacementConfirme(false);
            }}
          />

          {remplacer && (
            <>
              <Message ton="erreur">
                {`Les ${liste.length} élève(s) actuels et TOUTES leurs évaluations `}
                seront supprimés définitivement.
              </Message>
              {/*
                * Seconde confirmation, distincte de la case « remplacer ».
                * L'une exprime l'intention, l'autre atteste qu'on a lu ce
                * qu'elle détruit — un import remplaçant lancé par erreur
                * efface une année de suivi.
                */}
              <CaseACocher
                libelle="J'ai compris que les évaluations existantes seront perdues."
                checked={remplacementConfirme}
                onChange={(e) => setRemplacementConfirme(e.target.checked)}
              />
            </>
          )}

          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setImportOuvert(false)}>
              Annuler
            </Bouton>
            <Bouton
              type="submit"
              chargement={enCours}
              disabled={
                apercuImport.length === 0 || (remplacer && !remplacementConfirme)
              }
              variante={remplacer ? 'danger' : 'primary'}
            >
              {remplacer ? 'Remplacer la liste' : 'Importer'}
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>
    </div>
  );
}
